import React, { useState, useEffect, useMemo } from 'react';
import { ButtonFilled } from 'components/ui/Button';
import Cookies from 'js-cookie';
import ModalSmallInfoBase from 'components/ui/ModalSmallInfoBase';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import PopupInfo from 'components/CreatingTournament/PopupInfo';
import StakeSection from 'components/CreatingTournament/StakeSection';
import ChainSelector from 'components/CreatingTournament/ChainSelector';
import { logout } from 'api/authService';
import type { CreateTournamentPayload } from 'api/tournamentService';
import type { TournamentResponse } from 'types/tournament';

import {
  Wrap,
  Title,
  TextBold,
  Content,
  Text,
  Row,
  Col,
  ColMaxW,
  IconCalendar,
  Close,
  CalendarWrap,
  RowText,
  IconInfo,
  ErrorText,
  ComingSoonTooltip,
  TimePickerRow,
  TimeInput,
  TimeColon,
  UTCLabel,
  LocalTimeHint,
  DatetimePreview,
} from './styled';

import {
  getSmartDefault,
  padTime,
  buildUTCDate,
  formatDatetimeUTC,
  formatLocalTime,
  utcToCalendarDate,
} from './dateTimeHelpers';

import { useUser } from 'api/userService';
import { registerAnonymousUser } from 'utils/auth/anonymousRegistration';
import { CASH_TICKER, pairLabelOf } from 'utils/cash';
import { useNavigate } from 'react-router-dom';
import * as routes from 'constants/routes';
import { stakeSchemaFor, validateStakeAmount, limitsForChain } from 'validation/stake';
import { useStakeErrorMessage } from 'hooks/useStakeErrorMessage';
import { fetchTickers } from 'api/tickerService';
import type { Ticker } from 'api/tickerService';
import {
  CHAIN_ORDER,
  CHAIN_STAKE_CONFIG,
  ChainKey,
  DEFAULT_CHAIN,
} from 'config/chainStakeConfig';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

type TournamentType = '1vs1' | 'multiplayer';
type TradeType = 'Spot' | 'Margin';
type GameDuration = '5 min' | '30 min' | '1h' | '12h' | '1d' | '7d';

interface TournamentCreatingProps {
  onCreateTournament: (data: (CreateTournamentPayload & { stake: number })) => Promise<TournamentResponse>;
  setIsOpenedViewLoad: (isLoading: boolean) => void;
  errorInfo: string | null;
}

const CHAIN_TOOLTIP_DESCRIPTIONS: Record<ChainKey, string> = {
  solana: 'Solana: low fees, fast settlement, paid in SOL',
  polygon: 'Polygon: USDC-pegged stables, paid in POL',
};

const VISIBLE_CHAIN_DESCRIPTIONS = CHAIN_ORDER.map(
  (c) => CHAIN_TOOLTIP_DESCRIPTIONS[c],
);

const CHAIN_TOOLTIP_HEADER =
  CHAIN_ORDER.length === 1
    ? `Your tournament runs on ${CHAIN_STAKE_CONFIG[CHAIN_ORDER[0]].displayName}. Stake is paid in ${CHAIN_STAKE_CONFIG[CHAIN_ORDER[0]].ticker}.`
    : "Choose the blockchain your tournament runs on. Stake is paid in the chain's native token.";

interface FormData {
  tournament_type: TournamentType;
  trading_assets: string;
  trade_type: TradeType;
  game_duration: GameDuration;
  start_type: 'now' | 'scheduled';
  allow_anonymous_users?: boolean;
  stake: number;
  /** Chain key driving stake presets and ticker label */
  chain: ChainKey;
  /** Ticker ID resolved from /tickers; sent to backend as relation id */
  stake_currency?: number;
}

const TournamentCreating: React.FC<TournamentCreatingProps> = ({
  onCreateTournament,
  setIsOpenedViewLoad,
  errorInfo,
}) => {
  const navigate = useNavigate();
  const { isLoggedIn } = useUser();
  const [startDate, setStartDate] = useState<Value>(new Date());
  const minDate = new Date();
  const [isOpenedModalDate, setIsOpenedModalDate] = useState(false);
  const username = localStorage.getItem('username') ?? Cookies.get('username');

  const [draftDate, setDraftDate] = useState<Date>(new Date());
  const [hours, setHours] = useState<string>('');
  const [minutes, setMinutes] = useState<string>('');
  const [hasAccepted, setHasAccepted] = useState(false);

  const [showPopup, setShowPopup] = useState(false);
  const [showPopup1, setShowPopup1] = useState(false);
  const [showPopup2, setShowPopup2] = useState(false);
  const [showPopup3, setShowPopup3] = useState(false);
  const [showChainPopup, setShowChainPopup] = useState(false);

  const [show1vs1Tooltip, setShow1vs1Tooltip] = useState(false);

  const handleClose = () => setShowPopup(false);
  const handleClose1 = () => setShowPopup1(false);
  const handleClose2 = () => setShowPopup2(false);
  const handleClose3 = () => setShowPopup3(false);

  const [formData, setFormData] = useState<FormData>({
    tournament_type: 'multiplayer',
    trading_assets: 'all',
    trade_type: 'Spot',
    game_duration: '1h',
    start_type: 'now',
    allow_anonymous_users: true,
    stake: 0,
    chain: DEFAULT_CHAIN,
  });

  const stakeLimits = useMemo(() => limitsForChain(formData.chain), [formData.chain]);
  const tickerSymbol = CHAIN_STAKE_CONFIG[formData.chain].ticker;

  const stakeValidation = validateStakeAmount(formData.stake, stakeLimits);
  const stakeErrorMessage = useStakeErrorMessage({
    errorKey: stakeValidation.errorKey,
    context: {
      ticker: tickerSymbol,
      min: stakeLimits.min,
      max: stakeLimits.max,
    },
  });

  const [tickers, setTickers] = useState<Ticker[]>([]);
  useEffect(() => {
    fetchTickers()
      .then(setTickers)
      .catch(() => {
        // Non-blocking: backend default kicks in if stake_currency is missing.
      });
  }, []);

  // Resolve ticker.id for the selected chain whenever tickers or chain change.
  // Primary path uses `ticker.chain`; fallback matches by `short_name` so the
  // form keeps working on environments where the chain backfill migration
  // hasn't run yet and tickers come back with chain=null.
  useEffect(() => {
    if (!tickers.length) return;
    const expectedSymbol = CHAIN_STAKE_CONFIG[formData.chain].ticker;
    const match =
      tickers.find((t) => t.chain === formData.chain) ??
      tickers.find((t) => (t.short_name ?? '').toUpperCase() === expectedSymbol);
    if (match) {
      setFormData((prev) =>
        prev.stake_currency === match.id ? prev : { ...prev, stake_currency: match.id },
      );
    }
  }, [tickers, formData.chain]);

  useEffect(() => {
    if (formData.tournament_type === 'multiplayer' && !isLoggedIn) {
      const tournamentData = {
        ...formData,
        start_at:
          formData.start_type === 'now'
            ? new Date().toISOString()
            : startDate instanceof Date
              ? startDate.toISOString()
              : new Date().toISOString(),
      };
      localStorage.setItem('pendingTournamentData', JSON.stringify(tournamentData));
    }
  }, [formData, startDate, isLoggedIn]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value as any,
      ...(name === 'tournament_type' && {
        allow_anonymous_users: value === '1vs1',
      }),
    }));
  };

  const handleChainChange = (nextChain: ChainKey) => {
    if (nextChain === formData.chain) return;
    const nextConfig = CHAIN_STAKE_CONFIG[nextChain];
    setFormData((prev) => {
      const wasFree = prev.stake === 0;
      const wasCustom = prev.stake > 0 && !nextConfig.paidPresets.includes(prev.stake);
      let nextStake = prev.stake;
      if (!wasFree && !wasCustom) {
        // User had a paid preset of the previous chain — switch to the new
        // chain's first paid preset to keep the stake meaningful.
        nextStake = nextConfig.paidPresets[0];
      } else if (wasCustom) {
        // Custom amount from a different chain rarely fits — reset.
        nextStake = 0;
      }
      return {
        ...prev,
        chain: nextChain,
        stake: nextStake,
      };
    });
  };

  const handleStakeChange = (newStake: number) => {
    setFormData((prev) => ({
      ...prev,
      stake: newStake,
    }));
  };

  const handleSubmit = async () => {
    if (formData.start_type === 'scheduled') {
      if (!(startDate instanceof Date) || startDate.getTime() <= Date.now()) {
        return;
      }
    }

    try {
      await stakeSchemaFor(stakeLimits).validate(formData.stake);
    } catch (error) {
      return;
    }

    if (formData.tournament_type === 'multiplayer' && !isLoggedIn) {
      const tournamentData = {
        ...formData,
        start_at:
          formData.start_type === 'now'
            ? new Date().toISOString()
            : startDate instanceof Date
              ? startDate.toISOString()
              : new Date().toISOString(),
      };
      localStorage.setItem('pendingTournamentData', JSON.stringify(tournamentData));
      logout();
      navigate(routes.HOME);
      return;
    }

    setIsOpenedViewLoad(true);

    try {
      if (formData.tournament_type === '1vs1' && !isLoggedIn) {
        const credentials = await registerAnonymousUser();
        localStorage.setItem('username', credentials.username);
        localStorage.setItem('email', credentials.email);
      }

      const { chain: _chain, start_type: _startType, ...apiFields } = formData;
      const tournamentData = {
        ...apiFields,
        allow_anonymous_users: formData.tournament_type === '1vs1',
        start_at:
          formData.start_type === 'now'
            ? new Date().toISOString()
            : startDate instanceof Date
              ? startDate.toISOString()
              : new Date().toISOString(),
        // Always send stake_currency (also for Free) so backend records the
        // chosen chain consistently. Backend ignores it when stake === 0.
        ...(formData.stake_currency != null
          ? { stake_currency: formData.stake_currency }
          : {}),
      };

      await onCreateTournament(tournamentData);
    } catch (error) {
      console.error('Error creating tournament:', error);
      setIsOpenedViewLoad(false);
      alert('An error occurred while creating the tournament. Please try again.');
    }
  };

  const composedDate = draftDate instanceof Date && hours && minutes
    ? buildUTCDate(draftDate, hours, minutes)
    : null;
  const isFutureDateTime = composedDate ? composedDate.getTime() > Date.now() : false;

  const [preModalStartType, setPreModalStartType] = useState<'now' | 'scheduled'>('now');

  const showModalDate = () => {
    setPreModalStartType(formData.start_type);
    if (!hasAccepted) {
      const defaults = getSmartDefault();
      setHours(defaults.hours);
      setMinutes(defaults.minutes);
      setDraftDate(defaults.date);
    } else if (startDate instanceof Date) {
      setDraftDate(utcToCalendarDate(startDate));
      setHours(String(startDate.getUTCHours()).padStart(2, '0'));
      setMinutes(String(startDate.getUTCMinutes()).padStart(2, '0'));
    }
    setIsOpenedModalDate(true);
  };

  const handleAccept = () => {
    const finalDate = buildUTCDate(draftDate, hours, minutes);
    if (finalDate.getTime() <= Date.now()) return;
    setStartDate(finalDate);
    setHasAccepted(true);
    setFormData((prev) => ({ ...prev, start_type: 'scheduled' }));
    setIsOpenedModalDate(false);
  };

  const closePopUp = () => {
    setIsOpenedModalDate(false);
    setFormData((prev) => ({ ...prev, start_type: preModalStartType }));
  };

  return (
    <>
      <Wrap>
        <Title>Game parameters</Title>
        <TextBold>Organizer: <span>{username ? <>{username}</> : 'Guest'}</span></TextBold>
        <Content>
          <RowText>
            <Text>
              Chain
              <IconInfo onClick={() => setShowChainPopup(true)} />
            </Text>
            {showChainPopup && (
              <PopupInfo
                className="smallPopup"
                text={CHAIN_TOOLTIP_HEADER}
                subText={VISIBLE_CHAIN_DESCRIPTIONS[0] ?? ''}
                subTextSecond={VISIBLE_CHAIN_DESCRIPTIONS[1] ?? ''}
                handleClose={() => setShowChainPopup(false)}
              />
            )}
          </RowText>
          <ChainSelector
            value={formData.chain}
            onChange={handleChainChange}
          />

          <RowText>
            <Text>Type of the game<IconInfo onClick={() => setShowPopup(true)} /></Text>
            {showPopup && (
              <PopupInfo
                className="smallPopup"
                text="1vs1 game: Only 2 players – you and someone you invite via link or QR code"
                subText="Multiplayer game: Open tournament with unlimited spots for all to join. Access via Trading Battles website, link or QR code"
                subTextSecond=""
                handleClose={handleClose}
              />
            )}
          </RowText>
          <Row>
            <Col
              className="disabled"
              onClick={() => {
                setShow1vs1Tooltip(true);
                setTimeout(() => setShow1vs1Tooltip(false), 2500);
              }}
            >
              <input
                type="radio"
                name="tournament_type"
                value="1vs1"
                id="1vs1"
                disabled
              />
              <label htmlFor="1vs1">1vs1</label>
              {show1vs1Tooltip && (
                <ComingSoonTooltip>1vs1 mode coming soon!</ComingSoonTooltip>
              )}
            </Col>
            <Col>
              <input
                type="radio"
                name="tournament_type"
                value="multiplayer"
                id="multiplayer"
                checked={formData.tournament_type === 'multiplayer'}
                onChange={handleInputChange}
              />
              <label htmlFor="multiplayer">Multiplayer</label>
            </Col>
          </Row>

          <StakeSection
            chain={formData.chain}
            value={formData.stake}
            onChange={handleStakeChange}
            error={stakeErrorMessage ?? undefined}
          />

          <RowText>
            <Text>Trading assets<IconInfo onClick={() => setShowPopup1(true)} /></Text>
            {showPopup1 && (
              <PopupInfo
                className="smallPopup"
                text="Choose assets for your game:"
                subText={`${pairLabelOf('BTC')}: Only Bitcoin `}
                subTextSecond={`All: 50 largest cryptocurrencies by market cap, traded against ${CASH_TICKER}`}
                handleClose={handleClose1}
              />
            )}
          </RowText>

          <Row>
            <Col>
              <input
                type="radio"
                name="trading_assets"
                value="all"
                id="all"
                checked={formData.trading_assets === 'all'}
                onChange={handleInputChange}
              />
              <label htmlFor="all">All</label>
            </Col>
            <Col>
              <input
                type="radio"
                name="trading_assets"
                value="BTC/USDT"
                id="btc"
                checked={formData.trading_assets === 'BTC/USDT'}
                onChange={handleInputChange}
              />
              <label htmlFor="btc">{pairLabelOf('BTC')}</label>
            </Col>
          </Row>

          <RowText>
            <Text>Trade type<IconInfo onClick={() => setShowPopup2(true)} /></Text>
            {showPopup2 && (
              <PopupInfo
                className="smallPopup"
                text="Choose assets for your game:"
                subText="Spot: Trading with no leverage"
                subTextSecond="Margin: Trading with leverage (x1, x2, x5, x10, x25, x50), allowing for larger positions and higher potential gains or losses"
                handleClose={handleClose2}
              />
            )}
          </RowText>
          <Row>
            <Col>
              <input
                type="radio"
                name="trade_type"
                value="Spot"
                id="spot"
                checked={formData.trade_type === 'Spot'}
                onChange={handleInputChange}
              />
              <label htmlFor="spot">Spot</label>
            </Col>
            <Col>
              <input
                type="radio"
                name="trade_type"
                value="Margin"
                id="margin"
                checked={formData.trade_type === 'Margin'}
                onChange={handleInputChange}
              />
              <label htmlFor="margin">Margin</label>
            </Col>
          </Row>

          <RowText>
            <Text>Game duration</Text>
          </RowText>
          <Row className="RowLarge">
            {(['5 min', '30 min', '1h', '12h', '1d', '7d'] as const).map((duration) => (
              <ColMaxW key={duration}>
                <input
                  type="radio"
                  name="game_duration"
                  value={duration}
                  id={duration}
                  checked={formData.game_duration === duration}
                  onChange={handleInputChange}
                />
                <label htmlFor={duration}>{duration}</label>
              </ColMaxW>
            ))}
          </Row>

          <RowText>
            <Text>Start<IconInfo onClick={() => setShowPopup3(true)} /></Text>
            {showPopup3 && (
              <PopupInfo
                className="LargePopup"
                text="Choose when you want to start your game:"
                subText="NOW: 1vs1 – Game starts as soon as your opponent joins via QR code or link Multiplayer – Game starts instantly "
                subTextSecond="Choose Date: Multiplayer – Set a start date for the game, ideal for sharing and inviting others to your tournament"
                handleClose={handleClose3}
              />
            )}
          </RowText>
          <Row>
            <Col>
              <input
                type="radio"
                name="start_type"
                value="now"
                id="now"
                checked={formData.start_type === 'now'}
                onChange={handleInputChange}
              />
              <label htmlFor="now">Now</label>
            </Col>
            <Col>
              <input
                type="radio"
                name="start_type"
                value="scheduled"
                id="scheduled"
                checked={formData.start_type === 'scheduled'}
                onChange={handleInputChange}
              />
              <label htmlFor="scheduled" onClick={showModalDate}>
                <IconCalendar /> Choose date
              </label>
            </Col>
          </Row>
        </Content>

        {hasAccepted && formData.start_type === 'scheduled' && startDate instanceof Date && (
          <Text className="inner">
            Start: {formatDatetimeUTC(startDate)}
          </Text>
        )}

        <ErrorText>{errorInfo}</ErrorText>

        <ButtonFilled className="btnSubmit" onClick={handleSubmit}>
          {formData.stake > 0 ? 'Pay & Create' : 'Create'}
        </ButtonFilled>
      </Wrap>

      {isOpenedModalDate && (
        <ModalSmallInfoBase>
          <Close onClick={closePopUp}/>
          <CalendarWrap>
            <Title>Choose date</Title>
            <Text>when your game starts</Text>
            <Calendar
              onChange={(date: Value) => { if (date instanceof Date) setDraftDate(date); }}
              value={draftDate}
              minDate={minDate}
            />
            <TimePickerRow>
              <TimeInput
                type="text"
                inputMode="numeric"
                maxLength={2}
                aria-label="Hour (UTC)"
                value={hours}
                onChange={(e) => setHours(e.target.value.replace(/\D/g, ''))}
                onBlur={() => setHours(padTime(hours, 23))}
              />
              <TimeColon>:</TimeColon>
              <TimeInput
                type="text"
                inputMode="numeric"
                maxLength={2}
                aria-label="Minute (UTC)"
                value={minutes}
                onChange={(e) => setMinutes(e.target.value.replace(/\D/g, ''))}
                onBlur={() => setMinutes(padTime(minutes, 59))}
              />
              <UTCLabel>(UTC)</UTCLabel>
            </TimePickerRow>
            <LocalTimeHint>
              {composedDate ? formatLocalTime(composedDate) : ''}
            </LocalTimeHint>
            <DatetimePreview>
              {composedDate ? formatDatetimeUTC(composedDate) : ''}
            </DatetimePreview>
            {!isFutureDateTime && composedDate && (
              <ErrorText>Must be in the future</ErrorText>
            )}
            <ButtonFilled
              className="center"
              disabled={!isFutureDateTime}
              onClick={handleAccept}
            >
              Accept
            </ButtonFilled>
          </CalendarWrap>
        </ModalSmallInfoBase>
      )}
    </>
  );
};

export default TournamentCreating;
